export type ProcessRole = "admin" | "manager" | "client" | "contractor" | "employee";

export type BusinessEventEnvelope = {
  id: string;
  organizationId: string;
  type: string;
  schemaVersion: number;
  occurredAt: string;
  actor: {
    profileId: string | null;
    role: ProcessRole | "system";
  };
  subject: {
    type: string;
    id: string;
  };
  context: Record<string, unknown>;
  correlationId?: string | null;
  causationId?: string | null;
};

export type ProcessCondition = {
  field: string;
  operator:
    | "equals"
    | "not_equals"
    | "in"
    | "exists"
    | "greater_than"
    | "less_than";
  value?: unknown;
};

export type ProcessAssignee =
  | { kind: "role"; role: ProcessRole }
  | { kind: "profile"; profileId: string }
  | { kind: "event_actor" };

export type RequiredActionStep = {
  id: string;
  kind: "required_action";
  titleTemplate: string;
  descriptionTemplate: string;
  assignee: ProcessAssignee;
  dueAfterMinutes: number;
  remindersBeforeDueMinutes: number[];
  escalateAfterMinutes: number | null;
  requiresHumanDecision: boolean;
  allowedDecisions: string[];
  directLinkTemplate: string;
};

export type NotificationStep = {
  id: string;
  kind: "notification";
  level: "information" | "alert";
  titleTemplate: string;
  bodyTemplate: string;
  recipient: ProcessAssignee;
  channelIntents: Array<"internal" | "push" | "email" | "sms">;
};

export type ProcessStep = RequiredActionStep | NotificationStep;

export type ProcessDefinition = {
  id: string;
  key: string;
  name: string;
  version: number;
  status: "draft" | "active" | "retired";
  trigger: {
    eventTypes: string[];
    conditions: ProcessCondition[];
  };
  steps: ProcessStep[];
  publishedAt: string | null;
  effectiveFrom: string | null;
  effectiveUntil: string | null;
};

export type PlannedRequiredAction = {
  idempotencyKey: string;
  stepId: string;
  status: "open";
  title: string;
  description: string;
  assignee: ProcessAssignee;
  dueAt: string;
  reminderAt: string[];
  escalationAt: string | null;
  requiresHumanDecision: boolean;
  allowedDecisions: string[];
  directLink: string;
};

export type PlannedNotification = {
  idempotencyKey: string;
  stepId: string;
  level: "information" | "alert";
  title: string;
  body: string;
  recipient: ProcessAssignee;
  channelIntents: Array<"internal" | "push" | "email" | "sms">;
};

export type ProcessRunPlan = {
  idempotencyKey: string;
  organizationId: string;
  eventId: string;
  eventType: string;
  processDefinitionId: string;
  processKey: string;
  processVersion: number;
  actions: PlannedRequiredAction[];
  notifications: PlannedNotification[];
  explanation: {
    trigger: string;
    rules: string[];
    process: string;
  };
};

export type ProcessSimulationResult = {
  definitionId: string;
  processKey: string;
  processVersion: number;
  validationErrors: string[];
  eventTypeMatches: boolean;
  conditionResults: Array<{
    field: string;
    operator: ProcessCondition["operator"];
    expected: unknown;
    actual: unknown;
    matches: boolean;
  }>;
  matches: boolean;
  plan: ProcessRunPlan | null;
};

const ALLOWED_ROOTS = new Set([
  "event",
  "actor",
  "subject",
  "context",
]);

function eventValue(event: BusinessEventEnvelope, field: string): unknown {
  const [root, ...path] = field.split(".");
  if (!ALLOWED_ROOTS.has(root)) return undefined;
  let current: unknown =
    root === "event"
      ? {
          id: event.id,
          type: event.type,
          schemaVersion: event.schemaVersion,
          occurredAt: event.occurredAt,
          organizationId: event.organizationId,
        }
      : root === "actor"
        ? event.actor
        : root === "subject"
          ? event.subject
          : event.context;
  for (const part of path) {
    if (!current || typeof current !== "object" || !(part in current))
      return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

function conditionMatches(
  event: BusinessEventEnvelope,
  condition: ProcessCondition,
) {
  const actual = eventValue(event, condition.field);
  switch (condition.operator) {
    case "equals":
      return actual === condition.value;
    case "not_equals":
      return actual !== condition.value;
    case "in":
      return Array.isArray(condition.value) && condition.value.includes(actual);
    case "exists":
      return condition.value === false
        ? actual === undefined || actual === null
        : actual !== undefined && actual !== null;
    case "greater_than":
      return typeof actual === "number" &&
        typeof condition.value === "number" &&
        actual > condition.value;
    case "less_than":
      return typeof actual === "number" &&
        typeof condition.value === "number" &&
        actual < condition.value;
  }
}

function renderTemplate(template: string, event: BusinessEventEnvelope) {
  return template.replace(/\{\{\s*([^{}]+?)\s*\}\}/g, (_match, field) => {
    const value = eventValue(event, String(field).trim());
    return value == null ? "" : String(value);
  });
}

function addMinutes(value: string, minutes: number) {
  const date = new Date(value);
  return new Date(date.getTime() + minutes * 60_000).toISOString();
}

function processKey(definition: ProcessDefinition, eventId: string) {
  return [
    "process",
    definition.key,
    `v${definition.version}`,
    eventId,
  ]
    .map((part) => encodeURIComponent(part))
    .join(":");
}

function stepKey(runKey: string, stepId: string) {
  return `${runKey}:${encodeURIComponent(stepId)}`;
}

export function validateProcessDefinition(definition: ProcessDefinition) {
  const errors: string[] = [];
  if (!definition.id.trim()) errors.push("Identifiant de définition absent.");
  if (!definition.key.trim()) errors.push("Clé de processus absente.");
  if (!Number.isInteger(definition.version) || definition.version < 1)
    errors.push("La version du processus doit être un entier positif.");
  if (!definition.trigger.eventTypes.length)
    errors.push("Le processus doit écouter au moins un type d’événement.");
  if (!definition.steps.length)
    errors.push("Le processus doit comporter au moins une étape.");
  if (definition.status === "active" && !definition.publishedAt)
    errors.push("Un processus actif doit avoir une date de publication.");
  const stepIds = new Set<string>();
  for (const step of definition.steps) {
    if (!step.id.trim()) errors.push("Une étape possède un identifiant vide.");
    if (stepIds.has(step.id))
      errors.push(`L’étape ${step.id} est déclarée plusieurs fois.`);
    stepIds.add(step.id);
    if (step.kind === "required_action") {
      if (step.dueAfterMinutes < 0)
        errors.push(`Le délai de l’étape ${step.id} ne peut pas être négatif.`);
      if (step.remindersBeforeDueMinutes.some((value) => value < 0))
        errors.push(`Les rappels de l’étape ${step.id} doivent être positifs.`);
      if (step.escalateAfterMinutes != null && step.escalateAfterMinutes < 0)
        errors.push(`L’escalade de l’étape ${step.id} ne peut pas être négative.`);
      if (step.requiresHumanDecision && !step.allowedDecisions.length)
        errors.push(`L’étape ${step.id} doit définir les décisions permises.`);
    }
  }
  return errors;
}

function isEffective(definition: ProcessDefinition, occurredAt: string) {
  const instant = new Date(occurredAt).getTime();
  return definition.status === "active" &&
    (!definition.effectiveFrom ||
      instant >= new Date(definition.effectiveFrom).getTime()) &&
    (!definition.effectiveUntil ||
      instant <= new Date(definition.effectiveUntil).getTime());
}

function selectProcessVersions(
  event: BusinessEventEnvelope,
  definitions: ProcessDefinition[],
) {
  const selected = new Map<string, ProcessDefinition>();
  for (const definition of definitions) {
    if (validateProcessDefinition(definition).length) continue;
    if (!isEffective(definition, event.occurredAt)) continue;
    if (!definition.trigger.eventTypes.includes(event.type)) continue;
    if (
      !definition.trigger.conditions.every((condition) =>
        conditionMatches(event, condition),
      )
    )
      continue;
    const current = selected.get(definition.key);
    if (!current || definition.version > current.version)
      selected.set(definition.key, definition);
  }
  return [...selected.values()].sort((left, right) =>
    left.key.localeCompare(right.key),
  );
}

export function planBusinessEvent(
  event: BusinessEventEnvelope,
  definitions: ProcessDefinition[],
): ProcessRunPlan[] {
  if (!event.id.trim() || !event.organizationId.trim() || !event.type.trim())
    throw new Error("Événement métier incomplet.");
  if (!Number.isInteger(event.schemaVersion) || event.schemaVersion < 1)
    throw new Error("Version de schéma événement invalide.");
  if (Number.isNaN(new Date(event.occurredAt).getTime()))
    throw new Error("Date d’événement invalide.");

  return selectProcessVersions(event, definitions).map((definition) => {
    const runKey = processKey(definition, event.id);
    const actions: PlannedRequiredAction[] = [];
    const notifications: PlannedNotification[] = [];
    for (const step of definition.steps) {
      const idempotencyKey = stepKey(runKey, step.id);
      if (step.kind === "required_action") {
        const dueAt = addMinutes(event.occurredAt, step.dueAfterMinutes);
        actions.push({
          idempotencyKey,
          stepId: step.id,
          status: "open",
          title: renderTemplate(step.titleTemplate, event),
          description: renderTemplate(step.descriptionTemplate, event),
          assignee: step.assignee,
          dueAt,
          reminderAt: [...new Set(step.remindersBeforeDueMinutes)]
            .sort((left, right) => right - left)
            .map((minutes) => addMinutes(dueAt, -minutes)),
          escalationAt:
            step.escalateAfterMinutes == null
              ? null
              : addMinutes(dueAt, step.escalateAfterMinutes),
          requiresHumanDecision: step.requiresHumanDecision,
          allowedDecisions: [...step.allowedDecisions],
          directLink: renderTemplate(step.directLinkTemplate, event),
        });
      } else {
        notifications.push({
          idempotencyKey,
          stepId: step.id,
          level: step.level,
          title: renderTemplate(step.titleTemplate, event),
          body: renderTemplate(step.bodyTemplate, event),
          recipient: step.recipient,
          channelIntents: [...new Set(step.channelIntents)],
        });
      }
    }
    return {
      idempotencyKey: runKey,
      organizationId: event.organizationId,
      eventId: event.id,
      eventType: event.type,
      processDefinitionId: definition.id,
      processKey: definition.key,
      processVersion: definition.version,
      actions,
      notifications,
      explanation: {
        trigger: `Événement ${event.type} reçu le ${event.occurredAt}.`,
        rules: definition.trigger.conditions.map(
          (condition) =>
            `${condition.field} ${condition.operator} ${JSON.stringify(condition.value)}`,
        ),
        process: `${definition.name} · version ${definition.version}.`,
      },
    };
  });
}

export function simulateProcessDefinition(
  event: BusinessEventEnvelope,
  definition: ProcessDefinition,
): ProcessSimulationResult {
  // Reuse the public event validation without ever persisting or dispatching a plan.
  planBusinessEvent(event, []);
  const validationErrors = validateProcessDefinition(definition),
    eventTypeMatches = definition.trigger.eventTypes.includes(event.type),
    conditionResults = definition.trigger.conditions.map((condition) => {
      const actual = eventValue(event, condition.field);
      return {
        field: condition.field,
        operator: condition.operator,
        expected: condition.value,
        actual,
        matches: conditionMatches(event, condition),
      };
    }),
    matches =
      validationErrors.length === 0 &&
      eventTypeMatches &&
      conditionResults.every((condition) => condition.matches);
  if (!matches)
    return {
      definitionId: definition.id,
      processKey: definition.key,
      processVersion: definition.version,
      validationErrors,
      eventTypeMatches,
      conditionResults,
      matches,
      plan: null,
    };
  const simulatedDefinition: ProcessDefinition = {
      ...structuredClone(definition),
      status: "active",
      publishedAt: event.occurredAt,
      effectiveFrom: null,
      effectiveUntil: null,
    },
    plan = planBusinessEvent(event, [simulatedDefinition])[0] || null;
  return {
    definitionId: definition.id,
    processKey: definition.key,
    processVersion: definition.version,
    validationErrors,
    eventTypeMatches,
    conditionResults,
    matches: Boolean(plan),
    plan,
  };
}

export const MISSION_REPORT_REVIEW_PROCESS_V1: ProcessDefinition = {
  id: "process-mission-report-review-v1",
  key: "mission_report_review",
  name: "Contrôle d’un compte rendu de mission",
  version: 1,
  status: "active",
  trigger: {
    eventTypes: ["mission.report_submitted"],
    conditions: [
      { field: "subject.type", operator: "equals", value: "mission" },
      { field: "context.report_id", operator: "exists" },
    ],
  },
  steps: [
    {
      id: "notify-admin",
      kind: "notification",
      level: "information",
      titleTemplate: "Compte rendu reçu · {{context.client_name}}",
      bodyTemplate:
        "{{context.worker_name}} a transmis le compte rendu de la mission {{subject.id}}.",
      recipient: { kind: "role", role: "admin" },
      channelIntents: ["internal", "push"],
    },
    {
      id: "review-report",
      kind: "required_action",
      titleTemplate: "Contrôler le compte rendu · {{context.client_name}}",
      descriptionTemplate:
        "Lire les heures, observations et pièces transmises par {{context.worker_name}} avant validation.",
      assignee: { kind: "role", role: "admin" },
      dueAfterMinutes: 24 * 60,
      remindersBeforeDueMinutes: [4 * 60],
      escalateAfterMinutes: 4 * 60,
      requiresHumanDecision: true,
      allowedDecisions: ["approve", "request_changes"],
      directLinkTemplate:
        "/missions/{{subject.id}}?view=report&report={{context.report_id}}",
    },
  ],
  publishedAt: "2026-10-08T00:00:00.000Z",
  effectiveFrom: "2026-10-08T00:00:00.000Z",
  effectiveUntil: null,
};

export const MISSION_BILLING_READY_INFORMATION_V1: ProcessDefinition = {
  id: "process-mission-billing-ready-information-v1",
  key: "mission_billing_ready_information",
  name: "Mission validée et prête à facturer",
  version: 1,
  status: "active",
  trigger: {
    eventTypes: ["mission.report_reviewed"],
    conditions: [
      { field: "subject.type", operator: "equals", value: "mission" },
      { field: "context.outcome", operator: "equals", value: "approved" },
    ],
  },
  steps: [
    {
      id: "inform-admin-billing-ready",
      kind: "notification",
      level: "information",
      titleTemplate: "Mission prête à facturer · {{context.client_name}}",
      bodyTemplate:
        "Le compte rendu de {{context.worker_name}} a été validé. La mission apparaît dans les missions à facturer.",
      recipient: { kind: "role", role: "admin" },
      channelIntents: ["internal"],
    },
  ],
  publishedAt: "2026-10-24T00:00:00.000Z",
  effectiveFrom: "2026-10-24T00:00:00.000Z",
  effectiveUntil: null,
};

export const MISSION_REPORT_INCIDENT_REVIEW_V1: ProcessDefinition = {
  id: "process-mission-report-incident-review-v1",
  key: "mission_report_incident_review",
  name: "Traitement d’un incident signalé en mission",
  version: 1,
  status: "active",
  trigger: {
    eventTypes: ["mission.report_submitted"],
    conditions: [
      { field: "subject.type", operator: "equals", value: "mission" },
      { field: "context.report_id", operator: "exists" },
      { field: "context.result", operator: "equals", value: "issue" },
    ],
  },
  steps: [
    {
      id: "alert-admin-incident",
      kind: "notification",
      level: "alert",
      titleTemplate: "Incident terrain · {{context.client_name}}",
      bodyTemplate:
        "{{context.worker_name}} a signalé un incident. Ouvrez le compte rendu avant toute décision opérationnelle, commerciale ou financière.",
      recipient: { kind: "role", role: "admin" },
      channelIntents: ["internal"],
    },
    {
      id: "review-incident-report",
      kind: "required_action",
      titleTemplate: "Contrôler l’incident · {{context.client_name}}",
      descriptionTemplate:
        "Lire les observations et justificatifs transmis par {{context.worker_name}}, puis valider le compte rendu ou demander des corrections. Aucun ajustement financier n’est automatique.",
      assignee: { kind: "role", role: "admin" },
      dueAfterMinutes: 60,
      remindersBeforeDueMinutes: [30],
      escalateAfterMinutes: 60,
      requiresHumanDecision: true,
      allowedDecisions: ["approve", "request_changes"],
      directLinkTemplate:
        "/missions/{{subject.id}}?view=report&report={{context.report_id}}",
    },
  ],
  publishedAt: "2026-10-26T01:26:20.000Z",
  effectiveFrom: "2026-10-26T01:26:20.000Z",
  effectiveUntil: null,
};

export const MISSION_REPORT_FOLLOWUP_REVIEW_V1: ProcessDefinition = {
  id: "process-mission-report-followup-review-v1",
  key: "mission_report_followup_review",
  name: "Traitement d’une action complémentaire de mission",
  version: 1,
  status: "active",
  trigger: {
    eventTypes: ["mission.report_submitted"],
    conditions: [
      { field: "subject.type", operator: "equals", value: "mission" },
      { field: "context.report_id", operator: "exists" },
      { field: "context.result", operator: "equals", value: "followup" },
    ],
  },
  steps: [
    {
      id: "inform-admin-followup",
      kind: "notification",
      level: "information",
      titleTemplate: "Suivi de mission demandé · {{context.client_name}}",
      bodyTemplate:
        "{{context.worker_name}} indique qu’une action complémentaire est nécessaire. Le compte rendu doit être contrôlé avant sa planification.",
      recipient: { kind: "role", role: "admin" },
      channelIntents: ["internal"],
    },
    {
      id: "review-followup-report",
      kind: "required_action",
      titleTemplate: "Organiser le suivi · {{context.client_name}}",
      descriptionTemplate:
        "Contrôler la demande de {{context.worker_name}} et les éléments transmis avant de décider de la suite. Aucune mission ni facturation n’est créée automatiquement.",
      assignee: { kind: "role", role: "admin" },
      dueAfterMinutes: 240,
      remindersBeforeDueMinutes: [60],
      escalateAfterMinutes: 240,
      requiresHumanDecision: true,
      allowedDecisions: ["approve", "request_changes"],
      directLinkTemplate:
        "/missions/{{subject.id}}?view=report&report={{context.report_id}}",
    },
  ],
  publishedAt: "2026-10-26T01:26:20.000Z",
  effectiveFrom: "2026-10-26T01:26:20.000Z",
  effectiveUntil: null,
};

export const BILLING_INVOICE_STATUS_INFORMATION_V1: ProcessDefinition = {
  id: "process-billing-invoice-status-information-v1",
  key: "billing_invoice_status_information",
  name: "Information sur l’émission et le règlement d’une facture",
  version: 1,
  status: "active",
  trigger: {
    eventTypes: ["billing.invoice_status_changed"],
    conditions: [
      { field: "subject.type", operator: "equals", value: "invoice" },
      { field: "context.status", operator: "in", value: ["sent", "paid"] },
    ],
  },
  steps: [
    {
      id: "inform-admin-invoice-status",
      kind: "notification",
      level: "information",
      titleTemplate: "Facture {{context.status_label}} · {{context.invoice_number}}",
      bodyTemplate:
        "La facture de {{context.client_name}} est maintenant {{context.status_label}}. Le solde restant est de {{context.remaining_amount}} €.",
      recipient: { kind: "role", role: "admin" },
      channelIntents: ["internal"],
    },
  ],
  publishedAt: "2026-10-26T02:45:00.000Z",
  effectiveFrom: "2026-10-26T02:45:00.000Z",
  effectiveUntil: null,
};

export const BILLING_INVOICE_OVERDUE_REVIEW_V1: ProcessDefinition = {
  id: "process-billing-invoice-overdue-review-v1",
  key: "billing_invoice_overdue_review",
  name: "Traitement d’une facture échue",
  version: 1,
  status: "active",
  trigger: {
    eventTypes: ["billing.invoice_status_changed"],
    conditions: [
      { field: "subject.type", operator: "equals", value: "invoice" },
      { field: "context.status", operator: "equals", value: "overdue" },
      { field: "context.remaining_amount", operator: "greater_than", value: 0 },
    ],
  },
  steps: [
    {
      id: "alert-admin-invoice-overdue",
      kind: "notification",
      level: "alert",
      titleTemplate: "Facture échue · {{context.invoice_number}}",
      bodyTemplate:
        "{{context.client_name}} doit encore {{context.remaining_amount}} €. Contrôlez les règlements avant de préparer une relance.",
      recipient: { kind: "role", role: "admin" },
      channelIntents: ["internal"],
    },
    {
      id: "review-invoice-overdue",
      kind: "required_action",
      titleTemplate: "Traiter l’échéance · {{context.invoice_number}}",
      descriptionTemplate:
        "Vérifier le solde de {{context.remaining_amount}} € et la situation de {{context.client_name}}. Une relance reste préparée puis validée par une personne.",
      assignee: { kind: "role", role: "admin" },
      dueAfterMinutes: 1440,
      remindersBeforeDueMinutes: [240],
      escalateAfterMinutes: 2880,
      requiresHumanDecision: true,
      allowedDecisions: ["prepare_reminder", "payment_received"],
      directLinkTemplate: "/billing?invoice={{subject.id}}&view=receivables",
    },
  ],
  publishedAt: "2026-10-26T02:45:00.000Z",
  effectiveFrom: "2026-10-26T02:45:00.000Z",
  effectiveUntil: null,
};

export const BILLING_PAYMENT_INFORMATION_V1: ProcessDefinition = {
  id: "process-billing-payment-information-v1",
  key: "billing_payment_information",
  name: "Information sur un règlement enregistré",
  version: 1,
  status: "active",
  trigger: {
    eventTypes: ["billing.payment_recorded"],
    conditions: [
      { field: "subject.type", operator: "equals", value: "invoice" },
      { field: "context.payment_id", operator: "exists" },
    ],
  },
  steps: [
    {
      id: "inform-admin-payment-recorded",
      kind: "notification",
      level: "information",
      titleTemplate: "Règlement enregistré · {{context.invoice_number}}",
      bodyTemplate:
        "Un règlement de {{context.payment_amount}} € a été confirmé pour {{context.client_name}}. Solde restant : {{context.remaining_amount}} €.",
      recipient: { kind: "role", role: "admin" },
      channelIntents: ["internal"],
    },
  ],
  publishedAt: "2026-10-26T02:45:00.000Z",
  effectiveFrom: "2026-10-26T02:45:00.000Z",
  effectiveUntil: null,
};

export const BILLING_PARTIAL_PAYMENT_REVIEW_V1: ProcessDefinition = {
  id: "process-billing-partial-payment-review-v1",
  key: "billing_partial_payment_review",
  name: "Suivi d’un règlement partiel",
  version: 1,
  status: "active",
  trigger: {
    eventTypes: ["billing.payment_recorded"],
    conditions: [
      { field: "subject.type", operator: "equals", value: "invoice" },
      { field: "context.remaining_amount", operator: "greater_than", value: 0 },
    ],
  },
  steps: [
    {
      id: "alert-admin-partial-payment",
      kind: "notification",
      level: "alert",
      titleTemplate: "Règlement partiel · {{context.invoice_number}}",
      bodyTemplate:
        "Le règlement reçu ne solde pas la facture de {{context.client_name}} : {{context.remaining_amount}} € restent à encaisser.",
      recipient: { kind: "role", role: "admin" },
      channelIntents: ["internal"],
    },
    {
      id: "review-partial-payment",
      kind: "required_action",
      titleTemplate: "Suivre le solde · {{context.invoice_number}}",
      descriptionTemplate:
        "Contrôler l’affectation du règlement et le solde de {{context.remaining_amount}} €. L’action se clôt à la préparation réelle d’une relance ou au paiement complet.",
      assignee: { kind: "role", role: "admin" },
      dueAfterMinutes: 4320,
      remindersBeforeDueMinutes: [1440],
      escalateAfterMinutes: 2880,
      requiresHumanDecision: true,
      allowedDecisions: ["prepare_reminder", "payment_received"],
      directLinkTemplate: "/billing?invoice={{subject.id}}&view=receivables",
    },
  ],
  publishedAt: "2026-10-26T02:45:00.000Z",
  effectiveFrom: "2026-10-26T02:45:00.000Z",
  effectiveUntil: null,
};

export const INVENTORY_LOW_STOCK_REVIEW_V1: ProcessDefinition = {
  id: "process-inventory-low-stock-review-v1",
  key: "inventory_low_stock_review",
  name: "Contrôle d’un stock sous son seuil",
  version: 1,
  status: "active",
  trigger: {
    eventTypes: ["inventory.stock_level_changed"],
    conditions: [
      { field: "subject.type", operator: "equals", value: "inventory_item" },
      { field: "context.level_state", operator: "equals", value: "low" },
    ],
  },
  steps: [
    {
      id: "alert-admin-low-stock",
      kind: "notification",
      level: "alert",
      titleTemplate: "Stock sous le seuil · {{context.item_name}}",
      bodyTemplate:
        "Il reste {{context.current_quantity}} {{context.unit}} pour un seuil de {{context.minimum_quantity}}. Contrôlez le besoin avant tout achat.",
      recipient: { kind: "role", role: "admin" },
      channelIntents: ["internal"],
    },
    {
      id: "review-low-stock",
      kind: "required_action",
      titleTemplate: "Traiter le stock faible · {{context.item_name}}",
      descriptionTemplate:
        "Vérifier la consommation, le stock physique et le fournisseur. L’action se clôt avec un réapprovisionnement réel ou un besoin d’approvisionnement réellement préparé.",
      assignee: { kind: "role", role: "admin" },
      dueAfterMinutes: 1440,
      remindersBeforeDueMinutes: [240],
      escalateAfterMinutes: 1440,
      requiresHumanDecision: true,
      allowedDecisions: ["prepare_restock", "stock_replenished"],
      directLinkTemplate: "/inventory?item={{subject.id}}",
    },
  ],
  publishedAt: "2026-10-26T09:30:00.000Z",
  effectiveFrom: "2026-10-26T09:30:00.000Z",
  effectiveUntil: null,
};

export const INVENTORY_STOCK_RECOVERED_INFORMATION_V1: ProcessDefinition = {
  id: "process-inventory-stock-recovered-information-v1",
  key: "inventory_stock_recovered_information",
  name: "Retour du stock au-dessus de son seuil",
  version: 1,
  status: "active",
  trigger: {
    eventTypes: ["inventory.stock_level_changed"],
    conditions: [
      { field: "subject.type", operator: "equals", value: "inventory_item" },
      { field: "context.level_state", operator: "equals", value: "recovered" },
    ],
  },
  steps: [
    {
      id: "inform-admin-stock-recovered",
      kind: "notification",
      level: "information",
      titleTemplate: "Stock rétabli · {{context.item_name}}",
      bodyTemplate:
        "Le stock est revenu à {{context.current_quantity}} {{context.unit}}, au-dessus du seuil de {{context.minimum_quantity}}.",
      recipient: { kind: "role", role: "admin" },
      channelIntents: ["internal"],
    },
  ],
  publishedAt: "2026-10-26T09:30:00.000Z",
  effectiveFrom: "2026-10-26T09:30:00.000Z",
  effectiveUntil: null,
};

export const SUPPLIER_INVOICE_SUBMISSION_REVIEW_V1: ProcessDefinition = {
  id: "process-supplier-invoice-submission-review-v1",
  key: "supplier_invoice_submission_review",
  name: "Contrôle d’une facture sous-traitant déposée",
  version: 1,
  status: "active",
  trigger: {
    eventTypes: ["purchasing.supplier_invoice_status_changed"],
    conditions: [
      { field: "subject.type", operator: "equals", value: "supplier_invoice" },
      { field: "context.status", operator: "equals", value: "submitted" },
    ],
  },
  steps: [
    {
      id: "alert-admin-supplier-invoice-submitted",
      kind: "notification",
      level: "alert",
      titleTemplate: "Facture ST à contrôler · {{context.invoice_number}}",
      bodyTemplate:
        "{{context.worker_name}} a déposé une facture de {{context.total_inc_vat}} € TTC. Vérifiez les missions, le PDF et les montants avant toute validation.",
      recipient: { kind: "role", role: "admin" },
      channelIntents: ["internal"],
    },
    {
      id: "review-supplier-invoice",
      kind: "required_action",
      titleTemplate: "Contrôler la facture ST · {{context.invoice_number}}",
      descriptionTemplate:
        "Contrôler la facture de {{context.worker_name}}, ses missions et ses montants. Aucun paiement ni message externe n’est déclenché automatiquement.",
      assignee: { kind: "role", role: "admin" },
      dueAfterMinutes: 1440,
      remindersBeforeDueMinutes: [240],
      escalateAfterMinutes: 1440,
      requiresHumanDecision: true,
      allowedDecisions: ["approve", "reject"],
      directLinkTemplate:
        "/billing?supplier_invoice={{subject.id}}&view=supplier_invoices",
    },
  ],
  publishedAt: "2026-10-26T12:30:40.000Z",
  effectiveFrom: "2026-10-26T12:30:40.000Z",
  effectiveUntil: null,
};

export const SUPPLIER_INVOICE_PAYMENT_FOLLOWUP_V1: ProcessDefinition = {
  id: "process-supplier-invoice-payment-followup-v1",
  key: "supplier_invoice_payment_followup",
  name: "Suivi du règlement d’une facture sous-traitant validée",
  version: 1,
  status: "active",
  trigger: {
    eventTypes: ["purchasing.supplier_invoice_status_changed"],
    conditions: [
      { field: "subject.type", operator: "equals", value: "supplier_invoice" },
      { field: "context.status", operator: "equals", value: "approved" },
    ],
  },
  steps: [
    {
      id: "inform-admin-supplier-invoice-approved",
      kind: "notification",
      level: "information",
      titleTemplate: "Facture ST validée · {{context.invoice_number}}",
      bodyTemplate:
        "La facture de {{context.worker_name}} est validée. Échéance : {{context.due_on}}. Son règlement reste à enregistrer manuellement.",
      recipient: { kind: "role", role: "admin" },
      channelIntents: ["internal"],
    },
    {
      id: "record-supplier-invoice-payment",
      kind: "required_action",
      titleTemplate: "Suivre le règlement ST · {{context.invoice_number}}",
      descriptionTemplate:
        "Vérifier l’échéance et enregistrer le paiement réel de {{context.total_inc_vat}} € TTC. Cette action ne paie rien et n’envoie aucun message.",
      assignee: { kind: "role", role: "admin" },
      dueAfterMinutes: 1440,
      remindersBeforeDueMinutes: [240],
      escalateAfterMinutes: 2880,
      requiresHumanDecision: true,
      allowedDecisions: ["payment_recorded"],
      directLinkTemplate:
        "/billing?supplier_invoice={{subject.id}}&view=supplier_invoices",
    },
  ],
  publishedAt: "2026-10-26T12:30:40.000Z",
  effectiveFrom: "2026-10-26T12:30:40.000Z",
  effectiveUntil: null,
};

export const SUPPLIER_INVOICE_TERMINAL_INFORMATION_V1: ProcessDefinition = {
  id: "process-supplier-invoice-terminal-information-v1",
  key: "supplier_invoice_terminal_information",
  name: "Information sur la clôture d’une facture sous-traitant",
  version: 1,
  status: "active",
  trigger: {
    eventTypes: ["purchasing.supplier_invoice_status_changed"],
    conditions: [
      { field: "subject.type", operator: "equals", value: "supplier_invoice" },
      { field: "context.status", operator: "in", value: ["rejected", "paid"] },
    ],
  },
  steps: [
    {
      id: "inform-admin-supplier-invoice-terminal",
      kind: "notification",
      level: "information",
      titleTemplate:
        "Facture ST {{context.status_label}} · {{context.invoice_number}}",
      bodyTemplate:
        "La facture de {{context.worker_name}} est maintenant {{context.status_label}}. La décision et ses références restent historisées dans son dossier.",
      recipient: { kind: "role", role: "admin" },
      channelIntents: ["internal"],
    },
  ],
  publishedAt: "2026-10-26T12:30:40.000Z",
  effectiveFrom: "2026-10-26T12:30:40.000Z",
  effectiveUntil: null,
};

export const EXPENSE_REPORT_SUBMISSION_REVIEW_V1: ProcessDefinition = {
  id: "process-expense-report-submission-review-v1",
  key: "expense_report_submission_review",
  name: "Contrôle d’une note de frais déposée",
  version: 1,
  status: "active",
  trigger: {
    eventTypes: ["accounting.expense_report_status_changed"],
    conditions: [
      { field: "subject.type", operator: "equals", value: "expense_report" },
      { field: "context.status", operator: "equals", value: "pending" },
    ],
  },
  steps: [
    {
      id: "alert-admin-expense-pending",
      kind: "notification",
      level: "alert",
      titleTemplate: "Note de frais à contrôler · {{context.owner_name}}",
      bodyTemplate:
        "{{context.owner_name}} a déposé une dépense de {{context.amount_inc_vat}} € TTC. Vérifiez le justificatif, la mission et les montants avant toute validation.",
      recipient: { kind: "role", role: "admin" },
      channelIntents: ["internal"],
    },
    {
      id: "review-expense-report",
      kind: "required_action",
      titleTemplate: "Contrôler la note de frais · {{context.owner_name}}",
      descriptionTemplate:
        "Contrôler « {{context.description}} », son justificatif et son mode de paiement. Aucun remboursement ni message externe n’est déclenché automatiquement.",
      assignee: { kind: "role", role: "admin" },
      dueAfterMinutes: 1440,
      remindersBeforeDueMinutes: [240],
      escalateAfterMinutes: 1440,
      requiresHumanDecision: true,
      allowedDecisions: ["approve", "reject", "cancel"],
      directLinkTemplate: "/accounting?expense={{subject.id}}&view=expenses",
    },
  ],
  publishedAt: "2026-10-26T12:47:24.000Z",
  effectiveFrom: "2026-10-26T12:47:24.000Z",
  effectiveUntil: null,
};

export const EXPENSE_REPORT_REIMBURSEMENT_FOLLOWUP_V1: ProcessDefinition = {
  id: "process-expense-report-reimbursement-followup-v1",
  key: "expense_report_reimbursement_followup",
  name: "Suivi du remboursement d’une note de frais",
  version: 1,
  status: "active",
  trigger: {
    eventTypes: ["accounting.expense_report_status_changed"],
    conditions: [
      { field: "subject.type", operator: "equals", value: "expense_report" },
      { field: "context.status", operator: "equals", value: "approved" },
      {
        field: "context.reimbursement_status",
        operator: "equals",
        value: "pending",
      },
    ],
  },
  steps: [
    {
      id: "inform-admin-expense-approved-personal",
      kind: "notification",
      level: "information",
      titleTemplate: "Note validée à rembourser · {{context.owner_name}}",
      bodyTemplate:
        "La dépense personnelle de {{context.amount_inc_vat}} € TTC est validée. Le remboursement doit encore être réellement exécuté puis enregistré.",
      recipient: { kind: "role", role: "admin" },
      channelIntents: ["internal"],
    },
    {
      id: "record-expense-reimbursement",
      kind: "required_action",
      titleTemplate: "Suivre le remboursement · {{context.owner_name}}",
      descriptionTemplate:
        "Vérifier le paiement réel de {{context.amount_inc_vat}} € puis enregistrer sa date et sa référence. Cette action ne déclenche aucun virement.",
      assignee: { kind: "role", role: "admin" },
      dueAfterMinutes: 4320,
      remindersBeforeDueMinutes: [1440],
      escalateAfterMinutes: 2880,
      requiresHumanDecision: true,
      allowedDecisions: ["reimbursement_recorded"],
      directLinkTemplate: "/accounting?expense={{subject.id}}&view=expenses",
    },
  ],
  publishedAt: "2026-10-26T12:47:24.000Z",
  effectiveFrom: "2026-10-26T12:47:24.000Z",
  effectiveUntil: null,
};

export const EXPENSE_REPORT_STATUS_INFORMATION_V1: ProcessDefinition = {
  id: "process-expense-report-status-information-v1",
  key: "expense_report_status_information",
  name: "Information sur la décision d’une note de frais",
  version: 1,
  status: "active",
  trigger: {
    eventTypes: ["accounting.expense_report_status_changed"],
    conditions: [
      { field: "subject.type", operator: "equals", value: "expense_report" },
      {
        field: "context.status",
        operator: "in",
        value: ["approved", "rejected", "cancelled"],
      },
    ],
  },
  steps: [
    {
      id: "inform-admin-expense-status",
      kind: "notification",
      level: "information",
      titleTemplate:
        "Note de frais {{context.status_label}} · {{context.owner_name}}",
      bodyTemplate:
        "La dépense « {{context.description}} » est maintenant {{context.status_label}}. La décision reste historisée dans la comptabilité.",
      recipient: { kind: "role", role: "admin" },
      channelIntents: ["internal"],
    },
  ],
  publishedAt: "2026-10-26T12:47:24.000Z",
  effectiveFrom: "2026-10-26T12:47:24.000Z",
  effectiveUntil: null,
};

export const EXPENSE_REPORT_REIMBURSEMENT_INFORMATION_V1: ProcessDefinition = {
  id: "process-expense-report-reimbursement-information-v1",
  key: "expense_report_reimbursement_information",
  name: "Information sur un remboursement enregistré",
  version: 1,
  status: "active",
  trigger: {
    eventTypes: ["accounting.expense_reimbursement_recorded"],
    conditions: [
      { field: "subject.type", operator: "equals", value: "expense_report" },
      {
        field: "context.reimbursement_status",
        operator: "equals",
        value: "paid",
      },
    ],
  },
  steps: [
    {
      id: "inform-admin-expense-reimbursed",
      kind: "notification",
      level: "information",
      titleTemplate: "Remboursement enregistré · {{context.owner_name}}",
      bodyTemplate:
        "Le remboursement de {{context.amount_inc_vat}} € a été enregistré le {{context.reimbursed_on}} avec sa référence. Aucun paiement n’a été exécuté par ARD Manager.",
      recipient: { kind: "role", role: "admin" },
      channelIntents: ["internal"],
    },
  ],
  publishedAt: "2026-10-26T12:47:24.000Z",
  effectiveFrom: "2026-10-26T12:47:24.000Z",
  effectiveUntil: null,
};

export const PROCUREMENT_REQUIREMENT_REVIEW_V1: ProcessDefinition = {
  id: "process-procurement-requirement-review-v1",
  key: "procurement_requirement_review",
  name: "Contrôle d’un besoin d’approvisionnement",
  version: 1,
  status: "active",
  trigger: {
    eventTypes: ["procurement.requirement_prepared"],
    conditions: [
      {
        field: "subject.type",
        operator: "equals",
        value: "procurement_requirement",
      },
      { field: "context.status", operator: "equals", value: "to_review" },
    ],
  },
  steps: [
    {
      id: "inform-admin-procurement",
      kind: "notification",
      level: "information",
      titleTemplate: "Approvisionnement à contrôler · {{context.label}}",
      bodyTemplate:
        "Un besoin de {{context.quantity}} {{context.unit_label}} doit être vérifié avant toute commande.",
      recipient: { kind: "role", role: "admin" },
      channelIntents: ["internal"],
    },
    {
      id: "review-procurement",
      kind: "required_action",
      titleTemplate: "Contrôler l’approvisionnement · {{context.label}}",
      descriptionTemplate:
        "Vérifier fournisseur, quantité, coût et date nécessaire. Aucune commande ne sera envoyée automatiquement.",
      assignee: { kind: "role", role: "admin" },
      dueAfterMinutes: 1440,
      remindersBeforeDueMinutes: [240],
      escalateAfterMinutes: 480,
      requiresHumanDecision: true,
      allowedDecisions: ["approve", "cancel"],
      directLinkTemplate:
        "/inventory?view=procurement&requirement={{subject.id}}",
    },
  ],
  publishedAt: "2026-10-24T00:00:00.000Z",
  effectiveFrom: "2026-10-24T00:00:00.000Z",
  effectiveUntil: null,
};

export const WORKFORCE_LEAVE_REVIEW_V1: ProcessDefinition = {
  id: "process-workforce-leave-review-v1",
  key: "workforce_leave_review",
  name: "Contrôle d’une demande de congé",
  version: 1,
  status: "active",
  trigger: {
    eventTypes: ["workforce.leave_requested"],
    conditions: [
      { field: "subject.type", operator: "equals", value: "leave_request" },
      { field: "context.status", operator: "equals", value: "pending" },
    ],
  },
  steps: [
    {
      id: "inform-admin-leave",
      kind: "notification",
      level: "information",
      titleTemplate: "Congé à contrôler · {{context.worker_name}}",
      bodyTemplate:
        "Demande du {{context.starts_on}} au {{context.ends_on}} à examiner dans Équipe & RH.",
      recipient: { kind: "role", role: "admin" },
      channelIntents: ["internal"],
    },
    {
      id: "review-leave",
      kind: "required_action",
      titleTemplate: "Décider du congé · {{context.worker_name}}",
      descriptionTemplate:
        "Vérifier les dates, l’organisation du planning et le motif avant de valider ou refuser la demande.",
      assignee: { kind: "role", role: "admin" },
      dueAfterMinutes: 2880,
      remindersBeforeDueMinutes: [480],
      escalateAfterMinutes: 1440,
      requiresHumanDecision: true,
      allowedDecisions: ["approve", "reject"],
      directLinkTemplate: "/team?view=leave&request={{subject.id}}",
    },
  ],
  publishedAt: "2026-10-24T00:00:00.000Z",
  effectiveFrom: "2026-10-24T00:00:00.000Z",
  effectiveUntil: null,
};

export const CONTRACTOR_CANCELLATION_REVIEW_V1: ProcessDefinition = {
  id: "process-contractor-cancellation-review-v1",
  key: "contractor_cancellation_review",
  name: "Contrôle d’une annulation de mission par un sous-traitant",
  version: 1,
  status: "active",
  trigger: {
    eventTypes: ["mission.contractor_cancellation_submitted"],
    conditions: [
      {
        field: "subject.type",
        operator: "equals",
        value: "worker_cancellation_notice",
      },
      { field: "context.status", operator: "equals", value: "submitted" },
    ],
  },
  steps: [
    {
      id: "inform-admin-contractor-cancellation",
      kind: "notification",
      level: "alert",
      titleTemplate: "Annulation ST à contrôler · {{context.worker_name}}",
      bodyTemplate:
        "{{context.worker_name}} demande l’annulation de la mission {{context.client_name}} prévue le {{context.starts_at}}.",
      recipient: { kind: "role", role: "admin" },
      channelIntents: ["internal"],
    },
    {
      id: "review-contractor-cancellation",
      kind: "required_action",
      titleTemplate: "Décider de l’annulation ST · {{context.client_name}}",
      descriptionTemplate:
        "Contrôler le motif, le justificatif et la proposition contractuelle avant d’accepter ou refuser. Aucune réaffectation ni pénalité n’est automatique.",
      assignee: { kind: "role", role: "admin" },
      dueAfterMinutes: 60,
      remindersBeforeDueMinutes: [30],
      escalateAfterMinutes: 60,
      requiresHumanDecision: true,
      allowedDecisions: ["approve", "reject"],
      directLinkTemplate:
        "/missions?mission={{context.mission_id}}&view=changes&cancellation={{subject.id}}",
    },
  ],
  publishedAt: "2026-10-25T00:00:00.000Z",
  effectiveFrom: "2026-10-25T00:00:00.000Z",
  effectiveUntil: null,
};

export const CONTRACTOR_ASSIGNMENT_REFUSAL_FOLLOWUP_V1: ProcessDefinition = {
  id: "process-contractor-assignment-refusal-followup-v1",
  key: "contractor_assignment_refusal_followup",
  name: "Traitement d’un refus de mission par un sous-traitant",
  version: 1,
  status: "active",
  trigger: {
    eventTypes: ["mission.assignment_refused"],
    conditions: [
      {
        field: "subject.type",
        operator: "equals",
        value: "mission_assignment",
      },
      { field: "context.status", operator: "equals", value: "refused" },
    ],
  },
  steps: [
    {
      id: "alert-admin-assignment-refusal",
      kind: "notification",
      level: "alert",
      titleTemplate: "Mission refusée · {{context.worker_name}}",
      bodyTemplate:
        "{{context.worker_name}} a refusé la mission {{context.client_name}} prévue le {{context.starts_at}}. La réaffectation reste une décision administrative.",
      recipient: { kind: "role", role: "admin" },
      channelIntents: ["internal"],
    },
    {
      id: "resolve-assignment-refusal",
      kind: "required_action",
      titleTemplate: "Réaffecter ou annuler · {{context.client_name}}",
      descriptionTemplate:
        "Contrôler le motif du refus puis affecter réellement une autre personne ou annuler la mission. Aucun remplaçant ne sera choisi automatiquement.",
      assignee: { kind: "role", role: "admin" },
      dueAfterMinutes: 60,
      remindersBeforeDueMinutes: [30],
      escalateAfterMinutes: 60,
      requiresHumanDecision: true,
      allowedDecisions: ["reassign", "cancel_mission"],
      directLinkTemplate:
        "/missions?mission={{context.mission_id}}&view=assignment&assignment={{subject.id}}",
    },
  ],
  publishedAt: "2026-10-25T00:00:00.000Z",
  effectiveFrom: "2026-10-25T00:00:00.000Z",
  effectiveUntil: null,
};

export const COMMERCIAL_QUOTE_ACCEPTED_INFORMATION_V1: ProcessDefinition = {
  id: "process-quote-accepted-v1",
  key: "commercial_quote_accepted_information",
  name: "Devis accepté par le client",
  version: 1,
  status: "active",
  trigger: {
    eventTypes: ["commercial.quote_responded"],
    conditions: [
      { field: "subject.type", operator: "equals", value: "quote" },
      { field: "context.status", operator: "equals", value: "accepted" },
    ],
  },
  steps: [{
    id: "inform-admin-quote-accepted",
    kind: "notification",
    level: "information",
    titleTemplate: "Devis accepté · {{context.client_name}}",
    bodyTemplate: "Le devis {{context.quote_number}} de {{context.total_inc_vat}} € TTC a été accepté. La suite contractuelle et opérationnelle reste sous contrôle de l’administration.",
    recipient: { kind: "role", role: "admin" },
    channelIntents: ["internal"],
  }],
  publishedAt: "2026-10-25T16:58:40.000Z",
  effectiveFrom: "2026-10-25T16:58:40.000Z",
  effectiveUntil: null,
};

export const COMMERCIAL_QUOTE_REJECTED_ALERT_V1: ProcessDefinition = {
  id: "process-quote-rejected-v1",
  key: "commercial_quote_rejected_alert",
  name: "Devis refusé par le client",
  version: 1,
  status: "active",
  trigger: {
    eventTypes: ["commercial.quote_responded"],
    conditions: [
      { field: "subject.type", operator: "equals", value: "quote" },
      { field: "context.status", operator: "equals", value: "rejected" },
    ],
  },
  steps: [{
    id: "alert-admin-quote-rejected",
    kind: "notification",
    level: "alert",
    titleTemplate: "Devis refusé · {{context.client_name}}",
    bodyTemplate: "Le devis {{context.quote_number}} a été refusé. La décision est historisée ; aucune relance ni nouvelle proposition ne part automatiquement.",
    recipient: { kind: "role", role: "admin" },
    channelIntents: ["internal"],
  }],
  publishedAt: "2026-10-25T16:58:40.000Z",
  effectiveFrom: "2026-10-25T16:58:40.000Z",
  effectiveUntil: null,
};

export const TENANT_CONTRACT_SIGNATURE_FOLLOWUP_V1: ProcessDefinition = {
  id: "process-contract-signature-followup-v1",
  key: "tenant_contract_signature_followup",
  name: "Suivi d’un contrat envoyé",
  version: 1,
  status: "active",
  trigger: {
    eventTypes: ["contract.status_changed"],
    conditions: [
      { field: "context.status", operator: "equals", value: "sent" },
      { field: "subject.type", operator: "in", value: ["client_contract", "worker_contract"] },
    ],
  },
  steps: [
    {
      id: "inform-admin-contract-sent",
      kind: "notification",
      level: "information",
      titleTemplate: "Contrat envoyé · {{context.party_name}}",
      bodyTemplate: "Le contrat « {{context.contract_title}} » est indiqué comme envoyé. Le suivi reste interne tant qu’aucun prestataire de signature n’a confirmé une livraison.",
      recipient: { kind: "role", role: "admin" },
      channelIntents: ["internal"],
    },
    {
      id: "follow-contract-signature",
      kind: "required_action",
      titleTemplate: "Suivre la signature · {{context.party_name}}",
      descriptionTemplate: "Contrôler la preuve d’envoi et suivre la signature du contrat. Ne jamais considérer le document signé sans preuve réelle.",
      assignee: { kind: "role", role: "admin" },
      dueAfterMinutes: 10080,
      remindersBeforeDueMinutes: [2880],
      escalateAfterMinutes: 4320,
      requiresHumanDecision: true,
      allowedDecisions: ["signed", "remind", "cancel"],
      directLinkTemplate: "{{context.direct_link}}",
    },
  ],
  publishedAt: "2026-10-25T16:58:40.000Z",
  effectiveFrom: "2026-10-25T16:58:40.000Z",
  effectiveUntil: null,
};

export const TENANT_CONTRACT_ACTIVATION_REVIEW_V1: ProcessDefinition = {
  id: "process-contract-activation-v1",
  key: "tenant_contract_activation_review",
  name: "Activation d’un contrat signé",
  version: 1,
  status: "active",
  trigger: {
    eventTypes: ["contract.status_changed"],
    conditions: [
      { field: "context.status", operator: "equals", value: "signed" },
      { field: "subject.type", operator: "in", value: ["client_contract", "worker_contract"] },
    ],
  },
  steps: [
    {
      id: "inform-admin-contract-signed",
      kind: "notification",
      level: "information",
      titleTemplate: "Contrat signé · {{context.party_name}}",
      bodyTemplate: "Le contrat « {{context.contract_title}} » est indiqué comme signé. Son activation reste une décision administrative explicite.",
      recipient: { kind: "role", role: "admin" },
      channelIntents: ["internal"],
    },
    {
      id: "review-contract-activation",
      kind: "required_action",
      titleTemplate: "Contrôler puis activer · {{context.party_name}}",
      descriptionTemplate: "Relire la preuve de signature, les dates et les conditions avant d’activer le contrat.",
      assignee: { kind: "role", role: "admin" },
      dueAfterMinutes: 1440,
      remindersBeforeDueMinutes: [240],
      escalateAfterMinutes: 1440,
      requiresHumanDecision: true,
      allowedDecisions: ["activate", "request_correction", "terminate"],
      directLinkTemplate: "{{context.direct_link}}",
    },
  ],
  publishedAt: "2026-10-25T16:58:40.000Z",
  effectiveFrom: "2026-10-25T16:58:40.000Z",
  effectiveUntil: null,
};

export const TENANT_CONTRACT_ACTIVE_INFORMATION_V1: ProcessDefinition = {
  id: "process-contract-active-v1",
  key: "tenant_contract_active_information",
  name: "Contrat activé",
  version: 1,
  status: "active",
  trigger: {
    eventTypes: ["contract.status_changed"],
    conditions: [
      { field: "context.status", operator: "equals", value: "active" },
      { field: "subject.type", operator: "in", value: ["client_contract", "worker_contract"] },
    ],
  },
  steps: [{
    id: "inform-admin-contract-active",
    kind: "notification",
    level: "information",
    titleTemplate: "Contrat actif · {{context.party_name}}",
    bodyTemplate: "Le contrat « {{context.contract_title}} » est désormais actif. La décision et sa chronologie sont conservées dans le journal.",
    recipient: { kind: "role", role: "admin" },
    channelIntents: ["internal"],
  }],
  publishedAt: "2026-10-25T16:58:40.000Z",
  effectiveFrom: "2026-10-25T16:58:40.000Z",
  effectiveUntil: null,
};
